import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { SectionHeader } from "./Top3";
import EditableText from "./EditableText";
import { RowActions, AddRowButton } from "./RowActions";
import { useReorder } from "../lib/useReorder";
import { useSwipeToDelete } from "../lib/useSwipeToDelete";
import { useEditMode } from "../lib/EditModeContext";
import SourceBadge from "./SourceBadge";
import { toast } from "sonner";

const notifyTodoist = (res) => {
  if (!res?._todoist) return;
  if (res._todoist === "created") toast("Created in Todoist");
  else if (res._todoist === "updated") toast("Updated in Todoist");
  else if (res._todoist === "sync_failed") toast("Todoist sync failed", { description: "Local change kept." });
};

const TimelineRow = ({ r, rowProps, handleProps, dragId, overId, load, remove, notifyTodoist }) => {
  const { swipeProps, swipeStyle, revealPct } = useSwipeToDelete(() => remove(r));
  return (
    <div className="relative overflow-hidden">
      <div className="absolute inset-0 flex items-center justify-end pr-2 bg-[rgba(255,90,90,0.14)] text-[#FF7A7A] op-mono text-[10px] tracking-[0.24em] pointer-events-none" style={{ opacity: revealPct }}>
        DELETE
      </div>
      <div
        {...rowProps(r.id)}
        {...swipeProps}
        style={swipeStyle}
        data-dragging={dragId === r.id}
        data-drop-active={overId === r.id}
        className="op-editable-row flex items-baseline gap-4 group bg-[var(--op-panel-bg,rgba(10,12,20,1))]"
      >
        <EditableText
          value={r.time || ""}
          onSave={async (v) => { await api.patchTimeline(r.id, { time: v }); load(); }}
          className="op-mono text-xs text-[#A0A5B5] w-14 shrink-0"
          emptyPlaceholder="—"
          testid={`timeline-time-${r.id}`}
        />
        <EditableText
          value={r.label}
          onSave={async (v) => { const res = await api.patchTimeline(r.id, { label: v }); notifyTodoist(res); load(); }}
          className="text-white text-sm flex-1"
          testid={`timeline-label-${r.id}`}
          emptyPlaceholder="What's happening?"
        />
        <EditableText
          value={r.source || ""}
          onSave={async (v) => { await api.patchTimeline(r.id, { source: v.toUpperCase() }); load(); }}
          className="op-tag hidden md:inline"
          emptyPlaceholder="—"
          testid={`timeline-source-${r.id}`}
        />
        <RowActions onDelete={() => remove(r)} testid={`timeline-${r.id}`} handleProps={handleProps(r.id)} />
      </div>
    </div>
  );
};

const BUCKETS = ["NOW", "NEXT", "LATER", "PERSONAL"];

const Timeline = () => {
  const [items, setItems] = useState([]);
  const { editMode } = useEditMode();
  const load = () => api.timeline().then(setItems);
  useEffect(() => { load(); }, []);

  const remove = async (r) => {
    setItems((prev) => prev.filter((x) => x.id !== r.id));
    try {
      await api.deleteTimeline(r.id);
      toast("Row deleted", {
        action: {
          label: "UNDO",
          onClick: async () => {
            try {
              const { id: _, todoist_task_id: __, todoist_url: ___, ...body } = r;
              const restored = await api.createTimeline(body);
              setItems((prev) => [...prev, restored]);
              toast("Restored");
            } catch { toast("Undo failed"); }
          },
        },
        duration: 6000,
      });
    } catch { load(); }
  };
  const addTo = async (bucket) => {
    const created = await api.createTimeline({ bucket, label: "" });
    setItems((prev) => [...prev, created]);
  };
  const { rowProps, handleProps, dragId, overId } = useReorder(items, async (newIds) => {
    setItems((prev) => newIds.map((id) => prev.find((p) => p.id === id)).filter(Boolean));
    try { await api.reorderTimeline(newIds); } catch { load(); }
  });

  return (
    <section data-testid="timeline-section">
      <SectionHeader
        code="04" label="TODAY'S TIMELINE"
        caption="Calendar + tasks, in order of attention."
        labelKey="section.timeline.label"
        captionKey="section.timeline.caption"
      />
      <div className="op-panel p-6 md:p-8">
        <div className="relative pl-6">
          <div className="absolute left-[7px] top-2 bottom-2 w-px bg-white/10" aria-hidden="true" />
          {BUCKETS.map((b) => {
            const rows = items.filter((i) => i.bucket === b);
            const isEmpty = rows.length === 0;
            if (isEmpty && !editMode) return null;
            return (
              <div key={b} className="mb-8 last:mb-0" data-testid={`timeline-bucket-${b}`}>
                <div className="relative -left-6 flex items-center gap-3 mb-4">
                  <span
                    className={`inline-block w-[15px] h-[15px] border ${
                      b === "NOW" ? "border-[#00E5FF] bg-[rgba(0,229,255,0.15)]" : "border-white/20"
                    }`}
                    style={{ transform: "rotate(45deg)" }}
                  />
                  <span className={`op-mono text-[10px] tracking-[0.28em] ${b === "NOW" ? "text-[#00E5FF]" : "text-[#5E657D]"}`}>
                    [{b}]
                  </span>
                </div>
                <div className="space-y-3">
                  {rows.map((r) => (
                    <TimelineRow
                      key={r.id}
                      r={r}
                      rowProps={rowProps}
                      handleProps={handleProps}
                      dragId={dragId}
                      overId={overId}
                      load={load}
                      remove={remove}
                      notifyTodoist={notifyTodoist}
                    />
                  ))}
                  {editMode && (
                    <div className="pl-0 pt-2">
                      <AddRowButton onClick={() => addTo(b)} label={`ADD TO ${b}`} testid={`timeline-add-${b}`} />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Timeline;
